import { AuthenticationError, ForbiddenError } from '@multiplier/lib-shared-errors';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';

type ValidationIssue = {
  message?: string;
  instancePath?: string;
};

type ApiErrorResponse = {
  message?: string;
  error?: string;
  statusCode?: number;
  details?: {
    issues?: ValidationIssue[];
    // Handle nested errors from proxied responses
    body?: {
      message?: string;
      details?: {
        issues?: ValidationIssue[];
      };
    };
  };
};

/**
 * Gets a user-friendly message for HTTP status codes
 */
function getHttpStatusMessage(status: number | undefined): string | null {
  if (!status) {
    return null;
  }

  if (status === 401) {
    return 'Authentication expired. Please log in again.';
  }
  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  if (status >= 500) {
    return 'A server error occurred. Please try again later.';
  }

  return null;
}

/**
 * Extracts validation issue messages from API response
 */
function getValidationIssueMessage(data: ApiErrorResponse | undefined): string | null {
  const issues = data?.details?.issues ?? data?.details?.body?.details?.issues;

  if (!issues || issues.length === 0) {
    return null;
  }

  const messages = issues.map((issue) => issue.message).filter(Boolean);

  if (messages.length === 0) {
    return null;
  }

  return messages.join('; ');
}

/**
 * Extracts error message from Axios error
 */
function getAxiosErrorMessage(axiosError: AxiosError<ApiErrorResponse>): string {
  const responseData = axiosError.response?.data;

  const validationMessage = getValidationIssueMessage(responseData);
  if (validationMessage) {
    return validationMessage;
  }

  const responseMessage = responseData?.details?.body?.message || responseData?.message || responseData?.error;
  if (responseMessage) {
    return responseMessage;
  }

  const statusMessage = getHttpStatusMessage(axiosError.response?.status);
  if (statusMessage) {
    return statusMessage;
  }

  if (axiosError.code === 'ERR_NETWORK') {
    return 'Network error. Please check your connection.';
  }
  if (axiosError.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again.';
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Extracts a user-friendly error message from various error types
 */
export function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    return getAxiosErrorMessage(error as AxiosError<ApiErrorResponse>);
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Checks if the error is a client-side network error (not actionable on server side)
 */
function isClientNetworkError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    return axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED';
  }
  return false;
}

/**
 * Gets the HTTP status code from an Axios error, if present.
 */
function getAxiosErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    return (error as AxiosError<ApiErrorResponse>).response?.status;
  }
  if (error && error instanceof ForbiddenError) {
    return 403;
  }
  if (error && error instanceof AuthenticationError) {
    return 401;
  }
  return;
}

/**
 * Displays an error toast with a user-friendly message and sends the error to Sentry
 * @param error - The error to display
 * @param customMessage - Optional custom message to override the default
 */
export function showErrorToast(error: unknown, customMessage?: string): void {
  const message = customMessage || getErrorMessage(error);
  const status = getAxiosErrorStatus(error);

  const sentryContext = {
    contexts: {
      toast: {
        displayedMessage: message,
      },
    },
  };

  const isInfoLevel = isClientNetworkError(error) || (status !== undefined && status < 500);

  Sentry.captureException(error, {
    ...sentryContext,
    ...(isInfoLevel && { level: 'info' }),
  });

  toast.error(message);
}
